<template>
  <a v-if="href" :class="componentClasses" :href="href" :target="linkTarget" :rel="linkRel">
    <Icon v-if="iconBefore" class="stk-button-icon-before" :icon="iconBefore" />
    <Icon v-if="iconOnly" class="stk-button-icon-only" :icon="iconOnly" />
    <slot />
    <Icon v-if="iconAfter" class="stk-button-icon-after" :icon="iconAfter" />
    <div v-if="loader" class="stk-button-loader">
      <Loader />
    </div>
    <Loader v-if="loading" class="stk-button-loading"></Loader>
  </a>
  <router-link
    v-else-if="to"
    :to="to"
    :class="componentClasses"
    :disabled="disabled || loader || loading"
  >
    <Icon v-if="iconBefore" class="stk-button-icon-before" :icon="iconBefore" />
    <Icon v-if="iconOnly" class="stk-button-icon-only" :icon="iconOnly" />
    <slot />
    <Icon v-if="iconAfter" class="stk-button-icon-after" :icon="iconAfter" />
    <div v-if="loader" class="stk-button-loader">
      <Loader />
    </div>
    <Loader v-if="loading" class="stk-button-loading"></Loader>
  </router-link>
  <button v-else
    :class="componentClasses"
    :disabled="disabled || loader || loading"
    @click="$emit('click', $event)"
  >
    <Icon v-if="iconBefore" class="stk-button-icon-before" :icon="iconBefore" />
    <Icon v-if="iconOnly" class="stk-button-icon-only" :icon="iconOnly" />
    <slot />
    <Icon v-if="iconAfter" class="stk-button-icon-after" :icon="iconAfter" />
    <div v-if="loader" class="stk-button-loader">
      <Loader />
    </div>
    <Loader v-if="loading" class="stk-button-loading"></Loader>
  </button>
</template>

<script setup>
import { computed } from 'vue';
import Icon from '@stakeui/graphic/icon/Icon.vue';
import Loader from '@stakeui/graphic/loader/Loader.vue';

const props = defineProps({
  to: {
    type: [String, Object],
  },
  href: {
    type: String,
  },
  newTab: {
    type: Boolean,
    default: false,
  },
  variant: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  outlined: {
    type: Boolean,
    default: false,
  },
  fullwidth: {
    type: Boolean,
    default: false,
  },
  square: {
    type: Boolean,
    default: false,
  },
  loader: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  badge: {
    type: Boolean,
    default: false,
  },
  pressed: {
    type: Boolean,
    default: false,
  },
  faded: {
    type: Boolean,
    default: false,
  },
  static: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
  },
  resize: {
    type: String,
  },
  iconBefore: {
    type: String,
  },
  iconOnly: {
    type: String,
  },
  iconAfter: {
    type: String,
  },
  spacing: {
    type: String,
  },
  shade: {
    type: String,
  },
  borderless: {
    type: Boolean,
  },
});

const componentClasses = computed(() => [
  'stk-button',
  props.variant ? `is-${props.variant}` : '',
  props.shade ? `shaded shade-${props.shade}` : '',
  props.spacing ? `spacing-${props.spacing}` : '',
  props.loading ? 'is-loading' : '',
  props.iconOnly ? 'is-icon-only' : '',
  props.fullwidth ? 'is-fullwidth' : '',
  props.outlined ? 'is-outlined' : '',
  props.pressed ? 'is-pressed' : '',
  props.faded ? 'is-faded' : '',
  props.static && 'is-static',
  props.square ? 'is-square' : '',
  props.badge ? 'is-badge' : '',
  props.size ? `stk-size-${props.size}` : '',
  props.resize ? `stk-resize-${props.resize}` : '',
  props.borderless && 'is-borderless',
]);

const linkTarget = computed(() => (props.newTab ? '_blank' : '_self'));
const linkRel = computed(() => (props.newTab ? 'noopener noreferrer' : null));
</script>

<style>
.stk-button {
  --stk-icon-dimensions: 1.25em;

  cursor: pointer;
  font-weight: var(--stk-button-font-weight);
  justify-content: center;
  gap: 0.5em;
  padding-left: calc(var(--stk-button-padding-x) - var(--stk-control-border-width));
  padding-right: calc(var(--stk-button-padding-x) - var(--stk-control-border-width));
  text-align: center;

  strong {
    color: inherit;
  }

  .stk-icon {
    color: var(--stk-primary);
    transition-duration: 0ms;

    &:first-child {
      margin-left: -0.25em;
    }

    &:last-child {
      margin-right: -0.25em;
    }
  }

  .stk-badge {
    margin-top: -0.25em;
    margin-bottom: -0.25em;

    &:first-child {
      margin-left: -0.25em;
    }

    &:last-child {
      margin-right: -0.25em;
    }
  }

  &:hover {
    background-color: var(--stk-control-hover-background-color);
  }

  &:active,
  &.is-pressed {
    background-color: var(--stk-control-active-background-color);
    box-shadow: none;
  }

  &.is-faded:not(:hover) {
    box-shadow: none;
    opacity: 0.5;
  }

  &.is-static {
    pointer-events: none;
  }

  &:focus-visible {
    border-color: var(--stk-control-focus-border-color);
    outline-color: var(--stk-control-focus-outline-color);
    outline-style: var(--stk-control-focus-outline-style);
    outline-width: var(--stk-control-focus-outline-width);
  }

  &[disabled] {
    box-shadow: none;
    opacity: 0.5;
    pointer-events: none;
  }

  &.is-fullwidth {
    width: 100%;
    flex-grow: 1;
  }

  &.is-square {
    padding-left: calc(var(--stk-control-padding-y) - var(--stk-control-border-width));
    padding-right: calc(var(--stk-control-padding-y) - var(--stk-control-border-width));
    min-width: calc(
      calc(1em * var(--stk-control-line-height)) +
      calc(2 * var(--stk-control-padding-y))
    );
  }

  &.is-primary {
    --stk-control-background-color: var(--stk-primary-55);
    --stk-control-hover-background-color: var(--stk-primary-50);
    --stk-control-active-background-color: var(--stk-primary-45);
    --stk-control-border-color: var(--stk-primary-30);
    --stk-control-hover-border-color: var(--stk-primary-25);
    --stk-control-active-border-color: var(--stk-primary-20);
    --stk-control-color: var(--stk-primary-100);
    --stk-control-hover-color: var(--stk-primary-100);
    --stk-control-active-color: var(--stk-primary-100);

    .stk-icon {
      color: inherit;
    }

    &.is-outlined {
      --stk-control-background-color: var(--stk-scheme-main);
      --stk-control-border-color: var(--stk-primary);
      --stk-control-color: var(--stk-primary);
    }
  }

  &.is-success {
    --stk-control-background-color: var(--stk-success-50);
    --stk-control-hover-background-color: var(--stk-success-50);
    --stk-control-active-background-color: var(--stk-success-45);
    --stk-control-border-color: var(--stk-success-40);
    --stk-control-hover-border-color: var(--stk-success-35);
    --stk-control-active-border-color: var(--stk-success-30);
    --stk-control-color: var(--stk-success-100);
    --stk-control-hover-color: var(--stk-success-100);
    --stk-control-active-color: var(--stk-success-100);

    .stk-icon {
      color: inherit;
    }
  }

  &.is-info {
    --stk-control-background-color: var(--stk-info-55);
    --stk-control-hover-background-color: var(--stk-info-50);
    --stk-control-active-background-color: var(--stk-info-45);
    --stk-control-border-color: var(--stk-info-40);
    --stk-control-hover-border-color: var(--stk-info-35);
    --stk-control-active-border-color: var(--stk-info-30);
    --stk-control-color: var(--stk-info-100);
    --stk-control-hover-color: var(--stk-info-100);
    --stk-control-active-color: var(--stk-info-100);

    .stk-icon {
      color: inherit;
    }
  }

  &.is-danger {
    --stk-control-background-color: var(--stk-danger-55);
    --stk-control-hover-background-color: var(--stk-danger-50);
    --stk-control-active-background-color: var(--stk-danger-45);
    --stk-control-border-color: var(--stk-danger-30);
    --stk-control-hover-border-color: var(--stk-danger-25);
    --stk-control-active-border-color: var(--stk-danger-20);
    --stk-control-color: var(--stk-danger-100);
    --stk-control-hover-color: var(--stk-danger-100);
    --stk-control-active-color: var(--stk-danger-100);

    .stk-icon {
      color: inherit;
    }

    &.is-outlined {
      --stk-control-background-color: var(--stk-scheme-main);
      --stk-control-border-color: var(--stk-danger-70);
      --stk-control-color: var(--stk-danger-50);
    }
  }

  &.is-ask-anything {
    .stk-icon {
      color: var(--stk-ai-pink);
    }
  }

  &.shaded {
    .stk-icon {
      color: inherit;
    }
  }

  &.shade-success {
    color: var(--stk-success-45);

    &.is-pressed {
      background-color: var(--stk-success-95);
      border-color: var(--stk-success-60);
    }
  }

  &.shade-info {
    color: var(--stk-info-60);

    &.is-pressed {
      background-color: var(--stk-info-95);
      border-color: var(--stk-info-60);
    }
  }

  &.shade-warning {
    color: var(--stk-warning-50);

    &.is-pressed {
      background-color: var(--stk-warning-95);
      border-color: var(--stk-warning-60);
    }
  }

  &.shade-danger {
    color: var(--stk-danger-60);

    &.is-pressed {
      background-color: var(--stk-danger-95);
      border-color: var(--stk-danger-60);
    }
  }

  &.is-ghost {
    --stk-control-border-width: 0px;
    background: none;
    box-shadow: none;
    color: inherit;

    .stk-icon {
      color: inherit;
    }
  }

  &.spacing-loose {
    --stk-button-padding-x: 1.5em;
    --stk-control-padding-y: 0.875em;
  }

  &.spacing-tight {
    --stk-button-padding-x: 0.75em;
    --stk-control-padding-y: 0.375em;
  }

  &.spacing-none {
    --stk-button-padding-x: 0;
    --stk-control-padding-y: 0;
  }

  &.is-icon-only {
    --stk-button-padding-x: var(--stk-control-padding-y);

    .stk-icon {
      margin: 0 !important;
    }
  }

  &.is-loading {
    color: transparent;
    text-shadow: none;

    .stk-icon {
      color: inherit;
    }
  }

  /* Same size as the Badge component */
  &.is-badge {
    font-size: 0.875rem;
    line-height: 1rem;
    padding: 0.1625rem 0.375rem;
    border-radius: 0.5em;
    gap: 0.5em;

    .stk-icon {
      font-size: 0.75rem;

      &:first-child {
        margin-left: 0;
      }
    }

    .stk-button-loader {
      height: 1rem;
      margin-left: -0.125rem;
      margin-right: -0.125rem;
      width: 1rem;
    }
  }

  &.is-borderless {
    --stk-control-border-color: transparent;
    --stk-control-hover-border-color: transparent;
    --stk-control-active-border-color: transparent;
  }

  .stk-badge {
    margin-bottom: -0.25em;
    margin-top: -0.25em;

    &:not(:only-child) {
      &:first-child {
        margin-left: -0.5em;
      }

      &:last-child {
        margin-right: -0.5em;
      }
    }
  }
}

[data-stk-theme="dark"] {
  .stk-button {
    &.is-primary {
      --stk-control-border-color: var(--stk-primary-60);
      --stk-control-hover-border-color: var(--stk-primary-55);
      --stk-control-active-border-color: var(--stk-primary-50);
    }
  }
}

.stk-button-icon-before:first-child {
  margin-left: -0.25em;
}

.stk-button-icon-after:last-child {
  margin-right: -0.25em;
}

.stk-button-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(1em * var(--stk-control-line-height));
  flex-shrink: 0;
  width: calc(1em * var(--stk-control-line-height));
}

.stk-button-loading {
  --stk-loader-color: var( --stk-control-color);
  position: absolute;
  left: calc(50% - 0.5em);
  top: calc(50% - 0.5em);
}
</style>
